import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/api/client';
import {
  API_EVALUATION_REPORTS,
  API_EVALUATION_REPORTS_FINAL,
} from '@/api/endpoints';
import { useStudyYearPeriods } from '@/pages/Overview/hooks/useStudyYearPeriods';
import { getDefaultPeriod } from '@/pages/Overview/period';
import { unwrapAxiosResponse } from '@/utils/axios';
import { toApiDate } from '@/utils/date';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { createPdfObjectUrl, revokeObjectUrlLater } from '@/utils/file';

interface ReportRequest {
  fileName: string;
  path: string;
  params: Record<string, string>;
  errorMessage: ErrorMessage;
}

interface UseEvaluationReportPreviewResult {
  isReportOpen: boolean;
  reportUrl?: string;
  reportFileName: string;
  openStudyYearReport: () => void;
  openFinalReport: (parameters: string) => void;
  closeReport: () => void;
}

export const useEvaluationReportPreview = (
  onFailure: () => void,
): UseEvaluationReportPreviewResult => {
  const { t } = useTranslation();
  const { periods } = useStudyYearPeriods();
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportUrl, setReportUrl] = useState<string>();
  const [reportFileName, setReportFileName] = useState('');

  const { mutate } = useMutation({
    mutationFn: ({ path, params }: ReportRequest) =>
      apiClient
        .get<Blob>(path, { params, responseType: 'blob' })
        .then(unwrapAxiosResponse)
        .then(createPdfObjectUrl),
    onSuccess: setReportUrl,
    onError: (error, { errorMessage }) => {
      logRequestError(errorMessage, error);
      setIsReportOpen(false);
      onFailure();
    },
  });

  const openReport = (request: ReportRequest) => {
    setReportFileName(request.fileName);
    setIsReportOpen(true);
    mutate(request);
  };

  const openStudyYearReport = () => {
    const { from, to } = getDefaultPeriod(periods);

    openReport({
      fileName: `${t('overview.tabs.report')} ${toApiDate(from)}_${toApiDate(to)}.pdf`,
      path: API_EVALUATION_REPORTS,
      params: { from: toApiDate(from), to: toApiDate(to) },
      errorMessage: ErrorMessage.DownloadEvaluationReport,
    });
  };

  const openFinalReport = (parameters: string) =>
    openReport({
      fileName: `${t('mail.detail.finalEvaluationReport')}.pdf`,
      path: API_EVALUATION_REPORTS_FINAL,
      params: { parameters },
      errorMessage: ErrorMessage.DownloadFinalEvaluationReport,
    });

  const closeReport = () => {
    if (reportUrl) {
      revokeObjectUrlLater(reportUrl);
    }

    setReportUrl(undefined);
    setIsReportOpen(false);
  };

  return {
    isReportOpen,
    reportUrl,
    reportFileName,
    openStudyYearReport,
    openFinalReport,
    closeReport,
  };
};
